/**
 * Client-side image compression utility
 * Uses Canvas API to compress PNG and JPEG images
 */

/**
 * Compress an image file using client-side techniques
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width for the image (default: 1920)
 * @param {number} options.maxHeight - Maximum height for the image (default: 1920)
 * @param {number} options.quality - Image quality (0-1) for JPEGs (default: 0.6)
 * @param {boolean} options.preserveExif - Whether to preserve EXIF data for JPEGs (default: false)
 * @returns {Promise<Object>} - Compression result with original and compressed data
 */
export async function compressImage(file, options = {}) {
  // Default options
  const defaults = {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.6,
    preserveExif: false
  };
  
  const settings = { ...defaults, ...options };
  
  return new Promise((resolve, reject) => {
    try {
      // Create a file reader to read the image file
      const reader = new FileReader();
      
      reader.onload = function(event) {
        // Create an image element
        const img = new Image();
        
        img.onload = function() {
          // Check if resizing is needed
          let width = img.width;
          let height = img.height;
          
          if (width > settings.maxWidth || height > settings.maxHeight) {
            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
              if (width > settings.maxWidth) {
                height = Math.round(height * (settings.maxWidth / width));
                width = settings.maxWidth;
              }
            } else {
              if (height > settings.maxHeight) {
                width = Math.round(width * (settings.maxHeight / height));
                height = settings.maxHeight;
              }
            }
          }
          
          // Create a canvas element
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          // Draw the image on the canvas
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Get the file format
          const format = file.type.split('/')[1].toUpperCase();
          
          // Compress based on file type
          let compressedDataUrl;
          if (format === 'PNG') {
            // Use the tinyPNG-like compression for PNGs
            compressedDataUrl = compressPngTinyStyle(canvas, ctx, width, height);
          } else if (format === 'JPEG' || format === 'JPG') {
            compressedDataUrl = canvas.toDataURL('image/jpeg', settings.quality);
          } else {
            // Fallback for unsupported formats
            compressedDataUrl = canvas.toDataURL(file.type);
          }
          
          // Convert data URL to Blob
          const byteString = atob(compressedDataUrl.split(',')[1]);
          const mimeType = compressedDataUrl.split(',')[0].split(':')[1].split(';')[0];
          
          const arrayBuffer = new ArrayBuffer(byteString.length);
          const uint8Array = new Uint8Array(arrayBuffer);
          
          for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
          }
          
          const blob = new Blob([arrayBuffer], { type: mimeType });
          
          // Create a File object from the Blob
          const compressedFile = new File([blob], file.name, { 
            type: file.type,
            lastModified: file.lastModified
          });
          
          // Calculate savings
          const originalSize = file.size;
          const compressedSize = compressedFile.size;
          const savedBytes = originalSize - compressedSize;
          const savedPercentage = Math.round((savedBytes / originalSize) * 100);
          
          // Create object URLs for preview
          const originalUrl = URL.createObjectURL(file);
          const compressedUrl = URL.createObjectURL(compressedFile);
          
          // Resolve the promise with the results
          resolve({
            success: true,
            originalSize,
            compressedSize,
            savedBytes,
            savedPercentage,
            originalFile: file,
            compressedFile,
            originalUrl,
            compressedUrl,
            fileName: file.name
          });
        };
        
        img.onerror = function() {
          reject(new Error('Failed to load image'));
        };
        
        // Set the image source to the file content
        img.src = event.target.result;
      };
      
      reader.onerror = function() {
        reject(new Error('Failed to read file'));
      };
      
      // Read the file as a data URL
      reader.readAsDataURL(file);
      
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Compress PNG image using TinyPNG-like algorithm
 * Uses color quantization to reduce number of colors while maintaining quality
 * @param {HTMLCanvasElement} canvas - The canvas element with the image
 * @param {CanvasRenderingContext2D} ctx - The 2D rendering context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {string} - The compressed image as data URL
 */
function compressPngTinyStyle(canvas, ctx, width, height) {
  // Get image data for processing
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Create a new canvas for the optimized version
  const optimizedCanvas = document.createElement('canvas');
  optimizedCanvas.width = width;
  optimizedCanvas.height = height;
  const optimizedCtx = optimizedCanvas.getContext('2d');
  
  // Check for mostly opaque or mostly transparent image
  let transparentPixels = 0;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 128) transparentPixels++;
  }
  
  // If image has less than 1% transparent pixels, convert to JPEG for better compression
  if (transparentPixels / (width * height) < 0.01) {
    // Create a white background canvas
    const jpegCanvas = document.createElement('canvas');
    jpegCanvas.width = width;
    jpegCanvas.height = height;
    const jpegCtx = jpegCanvas.getContext('2d');
    
    // Fill with white background
    jpegCtx.fillStyle = '#FFFFFF';
    jpegCtx.fillRect(0, 0, width, height);
    
    // Draw the original image on top
    jpegCtx.drawImage(canvas, 0, 0);
    
    // Use JPEG compression which is much more efficient for photos
    const dataURL = jpegCanvas.toDataURL('image/jpeg', 0.75);
    
    // Clean up
    jpegCanvas.width = 0;
    jpegCanvas.height = 0;
    
    return dataURL;
  }
  
  // For images with transparency, use indexed-color PNG
  
  // Step 1: Convert to 8-bit indexed color mode (max 256 colors)
  // (Canvas doesn't natively support this, so we need to simulate it)
  
  // First build color frequency map
  const colorMap = new Map();
  let hasTransparency = false;
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    
    // Check for transparency
    if (a < 255) hasTransparency = true;
    
    // Skip fully transparent pixels for the color map
    if (a < 10) continue;
    
    // Create a color key
    const colorKey = `${r},${g},${b}`;
    
    // Count occurrence of this color
    if (colorMap.has(colorKey)) {
      colorMap.set(colorKey, colorMap.get(colorKey) + 1);
    } else {
      colorMap.set(colorKey, 1);
    }
  }
  
  // Convert color map to array and sort by frequency (descending)
  const colorFrequencies = [];
  for (const [colorKey, frequency] of colorMap.entries()) {
    const [r, g, b] = colorKey.split(',').map(Number);
    colorFrequencies.push({ r, g, b, frequency });
  }
  
  colorFrequencies.sort((a, b) => b.frequency - a.frequency);
  
  // Build an optimized palette
  const palette = [];
  const maxColors = 216; // 216 colors + transparency = 217 colors (under 256)
  
  // Always reserve the first palette entry for transparency if needed
  if (hasTransparency) {
    palette.push({ r: 0, g: 0, b: 0, a: 0 });
  }
  
  // Add most frequent colors to the palette
  for (let i = 0; i < Math.min(maxColors, colorFrequencies.length); i++) {
    palette.push({
      r: colorFrequencies[i].r,
      g: colorFrequencies[i].g,
      b: colorFrequencies[i].b,
      a: 255
    });
  }
  
  // Create lookup table for the nearest palette color
  const nearestColorCache = new Map();
  
  // Function to find nearest color in palette
  function findNearestColor(r, g, b, a) {
    // For transparent pixels, use the transparent color
    if (a < 10 && hasTransparency) {
      return 0; // First palette color is transparent
    }
    
    const colorKey = `${r},${g},${b}`;
    
    // Check if we've already computed this
    if (nearestColorCache.has(colorKey)) {
      return nearestColorCache.get(colorKey);
    }
    
    let minDistanceSquared = Infinity;
    let nearestIndex = 0;
    
    // Start from index 1 if we have transparency in the palette
    const startIndex = hasTransparency ? 1 : 0;
    
    for (let i = startIndex; i < palette.length; i++) {
      const pr = palette[i].r;
      const pg = palette[i].g;
      const pb = palette[i].b;
      
      // Use perceptual color distance (weighted Euclidean)
      // This gives more weight to green channel as human eyes are more sensitive to it
      const distSquared = 
        0.299 * (r - pr) * (r - pr) + 
        0.587 * (g - pg) * (g - pg) + 
        0.114 * (b - pb) * (b - pb);
      
      if (distSquared < minDistanceSquared) {
        minDistanceSquared = distSquared;
        nearestIndex = i;
      }
    }
    
    nearestColorCache.set(colorKey, nearestIndex);
    return nearestIndex;
  }
  
  // Create a new indexed image data (with palette lookup)
  const indexedImageData = new Uint8ClampedArray(width * height * 4);
  
  // Apply error diffusion dithering (Floyd-Steinberg)
  const errorR = new Float32Array(width * height);
  const errorG = new Float32Array(width * height);
  const errorB = new Float32Array(width * height);
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      
      // Get original color and add any diffused error
      let r = data[idx] + (errorR[y * width + x] || 0);
      let g = data[idx + 1] + (errorG[y * width + x] || 0);
      let b = data[idx + 2] + (errorB[y * width + x] || 0);
      const a = data[idx + 3];
      
      // Clamp values
      r = Math.max(0, Math.min(255, Math.round(r)));
      g = Math.max(0, Math.min(255, Math.round(g)));
      b = Math.max(0, Math.min(255, Math.round(b)));
      
      // Find nearest color in palette
      const paletteIndex = findNearestColor(r, g, b, a);
      const paletteColor = palette[paletteIndex];
      
      // Set pixel to palette color
      indexedImageData[idx] = paletteColor.r;
      indexedImageData[idx + 1] = paletteColor.g;
      indexedImageData[idx + 2] = paletteColor.b;
      indexedImageData[idx + 3] = paletteColor.a !== undefined ? paletteColor.a : 255;
      
      // Calculate quantization error
      const errR = r - paletteColor.r;
      const errG = g - paletteColor.g;
      const errB = b - paletteColor.b;
      
      // Distribute error to neighboring pixels
      if (x + 1 < width) {
        errorR[y * width + (x + 1)] = (errorR[y * width + (x + 1)] || 0) + errR * 7/16;
        errorG[y * width + (x + 1)] = (errorG[y * width + (x + 1)] || 0) + errG * 7/16;
        errorB[y * width + (x + 1)] = (errorB[y * width + (x + 1)] || 0) + errB * 7/16;
      }
      
      if (y + 1 < height) {
        if (x > 0) {
          errorR[(y + 1) * width + (x - 1)] = (errorR[(y + 1) * width + (x - 1)] || 0) + errR * 3/16;
          errorG[(y + 1) * width + (x - 1)] = (errorG[(y + 1) * width + (x - 1)] || 0) + errG * 3/16;
          errorB[(y + 1) * width + (x - 1)] = (errorB[(y + 1) * width + (x - 1)] || 0) + errB * 3/16;
        }
        
        errorR[(y + 1) * width + x] = (errorR[(y + 1) * width + x] || 0) + errR * 5/16;
        errorG[(y + 1) * width + x] = (errorG[(y + 1) * width + x] || 0) + errG * 5/16;
        errorB[(y + 1) * width + x] = (errorB[(y + 1) * width + x] || 0) + errB * 5/16;
        
        if (x + 1 < width) {
          errorR[(y + 1) * width + (x + 1)] = (errorR[(y + 1) * width + (x + 1)] || 0) + errR * 1/16;
          errorG[(y + 1) * width + (x + 1)] = (errorG[(y + 1) * width + (x + 1)] || 0) + errG * 1/16;
          errorB[(y + 1) * width + (x + 1)] = (errorB[(y + 1) * width + (x + 1)] || 0) + errB * 1/16;
        }
      }
    }
  }
  
  // Create a new imagedata object with the indexed colors
  const newImageData = new ImageData(indexedImageData, width, height);
  
  // Put the indexed image data on the optimized canvas
  optimizedCtx.putImageData(newImageData, 0, 0);
  
  // Convert the optimized canvas to a data URL
  // Use PNG format for the result (8-bit indexed PNG)
  const dataURL = optimizedCanvas.toDataURL('image/png');
  
  // Clean up
  optimizedCanvas.width = 0;
  optimizedCanvas.height = 0;
  
  return dataURL;
}

/**
 * Batch compress multiple image files
 * @param {File[]} files - Array of image files to compress
 * @param {Object} options - Compression options
 * @returns {Promise<Object[]>} - Array of compression results
 */
export async function batchCompressImages(files, options = {}) {
  const results = [];
  
  for (const file of files) {
    try {
      const result = await compressImage(file, options);
      results.push(result);
    } catch (error) {
      console.error(`Error compressing ${file.name}:`, error);
      // Add a failed result to the array
      results.push({
        success: false,
        fileName: file.name,
        error: error.message || 'Failed to compress image'
      });
    }
  }
  
  return results;
}

/**
 * Helper function to download a compressed image
 * @param {Object} result - Compression result
 */
export function downloadCompressedImage(result) {
  const link = document.createElement('a');
  
  // Get file extension
  const fileExt = result.fileName.split('.').pop().toLowerCase();
  
  // Create a name with "compressed-" prefix
  const nameWithoutExt = result.fileName.substring(0, result.fileName.lastIndexOf('.'));
  const downloadName = `compressed-${nameWithoutExt}.${fileExt}`;
  
  // Set link attributes
  link.href = result.compressedUrl;
  link.download = downloadName;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);
}

/**
 * Helper function to create and download a zip file containing multiple compressed images
 * @param {Object[]} results - Array of compression results
 * @returns {Promise<void>}
 */
export async function downloadAsZip(results) {
  // Check if JSZip is available from window (global)
  if (typeof window.JSZip !== 'function') {
    throw new Error('JSZip library is required for creating zip files');
  }
  
  const zip = new window.JSZip();
  
  // Add each file to the zip
  for (const result of results) {
    if (!result.success) continue;
    
    // Get the file
    const response = await fetch(result.compressedUrl);
    const blob = await response.blob();
    
    // Get file extension
    const fileExt = result.fileName.split('.').pop().toLowerCase();
    
    // Create a name with "compressed-" prefix
    const nameWithoutExt = result.fileName.substring(0, result.fileName.lastIndexOf('.'));
    const downloadName = `compressed-${nameWithoutExt}.${fileExt}`;
    
    // Add to zip
    zip.file(downloadName, blob);
  }
  
  // Generate the zip file
  const content = await zip.generateAsync({ type: 'blob' });
  
  // Create download link
  const link = document.createElement('a');
  link.href = URL.createObjectURL(content);
  link.download = 'compressed-images.zip';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  }, 100);
} 